import {ReactElement} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import {Box, DialogTitle, IconButton, Typography} from '@mui/material';
import {ReactComponent as CloseIcon} from '../../../assests/svg/close_icon.svg';
import AurButton from '../aur-button/AurButton';
import AurIcon, {IconNames, IconColors} from '../../common/aur-icon/AurIcon';
import {t} from 'i18next';

export type DialogProps = {
  labelActionOk?: string;
  labelActionCancel?: string;
  dialogTitle?: string;
  popupTitle?: string;
  popupContent?: string | React.ReactNode;
  isTextContent?: boolean; // if true then content will be displayed inside Typography otherwise inside Box
  onOk: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  buttonStyle?: string;
  className?: string;
  cancleLabelClassName?: string;
  okLabelClassName?: string;
  showDialog: boolean;
  dismissable?: boolean; // optional prop to control close button
  type?: 'alert' | 'default'; // optional prop for dialog type
  showActionButtons?: boolean;
  showTitleInline?: boolean;
  infoIcon?: ReactElement;
  onOkDisabled?: boolean;
  cancelActionTabIndex?: number;
  okActionTabIndex?: number;
  cancelButtonTitle?: string;
  confirmBtnId?: string;
};

const AurDialog: React.FC<DialogProps> = (props) => {
  const {
    showDialog,
    dialogTitle,
    popupTitle,
    popupContent,
    labelActionOk,
    labelActionCancel,
    onCancel,
    onOk,
    className = '',
    buttonStyle = 'sideBySide',
    dismissable = true,
    type = 'default',
    showActionButtons = true,
    showTitleInline = false,
    cancleLabelClassName = '',
    okLabelClassName = '',
    infoIcon,
    onOkDisabled = false,
    cancelActionTabIndex = 0,
    okActionTabIndex = 0,
    cancelButtonTitle = t('common.stayLoggedInMsg'),
    isTextContent = true,
    confirmBtnId,
    ...dialogProps
  } = props;

  return (
    <Dialog
      open={showDialog}
      className={`popup confirmation-popup-modal ${className}`}
      data-testid="popup-dialog"
      {...dialogProps}
    >
      {dialogTitle && !dismissable ? (
        <DialogTitle className="popup-dialog-title">
          <Typography className="typo-text label2_size popup-dialog-title-text">
            {dialogTitle}
          </Typography>
        </DialogTitle>
      ) : null}

      {dismissable ? (
        <DialogTitle
          className={
            showTitleInline ? 'popup-dialog-title-inline' : 'popup-dialog-title'
          }
        >
          {dialogTitle ? (
            <Typography
              className={
                showTitleInline
                  ? 'typo-text headline2_size popup-dialog-title-text'
                  : 'typo-text label2_size popup-dialog-title-text'
              }
            >
              {dialogTitle}
            </Typography>
          ) : null}
          <IconButton
            onClick={onCancel}
            title="Close Popup"
            data-testid="icon-close-button"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      ) : null}

      <DialogContent>
        {!showTitleInline && (
          <Box className="popup-dialog-content-header">
            {
              type === 'alert' &&
                //<div className="info-icon">
                (infoIcon ?? (
                  <AurIcon
                    icon={IconNames.Info}
                    color={IconColors.Critical}
                    customClassName=" info-icon-custom"
                    size="large"
                  />
                ))
              //</div>
            }
            <Typography
              className="popup-dialog-content-header-text"
              tabIndex={0}
            >
              {popupTitle}
            </Typography>
          </Box>
        )}

        {isTextContent ? (
          <Typography className="popup-dialog-content-body-text" tabIndex={0}>
            {popupContent}
          </Typography>
        ) : (
          <Box className="popup-dialog-content">{popupContent}</Box>
        )}
      </DialogContent>
      {showActionButtons && (
        <div className={`${buttonStyle}`}>
          <DialogActions className="popup-dialog-action">
            {buttonStyle != 'single' && (
              <AurButton
                classNames={`typo-text buttonLarge_size ${cancleLabelClassName}`}
                type="exit"
                onClick={onCancel}
                label={labelActionCancel}
                title={cancelButtonTitle}
                tabIndex={cancelActionTabIndex}
                dataTestId="dialog-cancel-button"
              />
            )}
            <AurButton
              classNames={`typo-text buttonLarge_size ${okLabelClassName}`}
              type="continue"
              onClick={onOk}
              label={labelActionOk}
              tabIndex={okActionTabIndex}
              dataTestId="dialog-confirm-button"
              disabled={onOkDisabled}
              id={confirmBtnId}
            />
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
};

export default AurDialog;
